import { Box } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getActiveStep, getActiveSubStage, getActiveTicket } from 'store/ticket-service/selector';
import { setDetailedViewTicketSummary, setEditMode } from 'store/ticket-service/ticketSlice';
import { IUserStoryAcceptanceCriteria, IUserStoryScenario } from 'store/ticket-service/types';

import { NamespacesList, SharePermissions, TicketStages } from 'core/enums/common';

import styles from 'components/shared/CustomDropdown/UserStoryDropdown/UserStoryDropdown.module.scss';
import { RegularText } from 'components/shared/CustomText/RegularText';

import { ReactComponent as CloseStories } from 'assets/icons/close-icon.svg';
import { ReactComponent as EditStories } from 'assets/icons/pencil.svg';

import * as Styled from './styled';

interface IDetailedStoryViewProps {
	scenarios: Array<IUserStoryScenario>;
	acceptanceCriteria: Array<IUserStoryAcceptanceCriteria>;
}

export const DetailedStoryView: FC<IDetailedStoryViewProps> = ({
	scenarios,
	acceptanceCriteria,
}) => {
	const { t: generalStepTranslate } = useTranslation(NamespacesList['generalStep']);

	const dispatch = useDispatch();
	const activeStep = useSelector(getActiveStep);
	const activeSubStage = useSelector(getActiveSubStage);
	const activeTicket = useSelector(getActiveTicket);

	const [activeTab, setActiveTab] = useState(generalStepTranslate('commonText.scenarios'));

	const tabs: Array<string> = useMemo(() => {
		const tabsArray = [];

		if (scenarios?.length) tabsArray.push(generalStepTranslate('commonText.scenarios'));

		if (acceptanceCriteria?.length && activeStep !== TicketStages['useCases'])
			tabsArray.push(generalStepTranslate('commonText.acceptanceCriteria'));

		return tabsArray;
	}, [scenarios, acceptanceCriteria, activeStep]);

	const handleEditButtonClick = () => {
		dispatch(setEditMode(true));
	};

	const handleViewClose = () => {
		dispatch(setDetailedViewTicketSummary(null));
	};

	const isGeneralStep = useMemo(() => activeStep === TicketStages.general, [activeStep]);

	const isUseCases = useMemo(() => activeStep === TicketStages.useCases, [activeStep]);

	const isNotSharedWithReadPermission = useMemo(() => {
		if (activeTicket?.permissions?.length) {
			const currentPermission = activeTicket.permissions.find(
				(permission) =>
					permission.ticketCurrentStep === TicketStages['general'] &&
					(permission.subStageEnum === null || permission.subStageEnum === activeSubStage)
			);

			if (currentPermission) return currentPermission.permission !== SharePermissions.Read;
		}

		return true;
	}, [activeTicket, activeSubStage]);

	return (
		<Box
			sx={{
				width: '50%',
				boxShadow: '0px 4px 16px 0px #2867830F',
				borderRadius: '8px',
				position: 'sticky',
				height: 'fit-content',
				top: 0,
			}}
		>
			<Box
				sx={{
					boxSizing: 'border-box',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
					padding: '12px',
					marginBottom: '12px',
					borderBottom: '1px solid #E7E8EC',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						gap: '12px',
					}}
				>
					{tabs?.map((tab, index) => (
						<Styled.DetailedStoryViewTab
							key={index}
							active={tab === activeTab}
							onClick={() => setActiveTab(tab)}
						>
							{tab}
						</Styled.DetailedStoryViewTab>
					))}
				</Box>
				<Box
					sx={{
						display: 'flex',
						gap: '24px',
					}}
				>
					{isGeneralStep && isNotSharedWithReadPermission ? (
						<EditStories style={{ cursor: 'pointer' }} onClick={handleEditButtonClick} />
					) : null}
					<CloseStories style={{ cursor: 'pointer' }} onClick={handleViewClose} />
				</Box>
			</Box>
			{scenarios.length && activeTab === generalStepTranslate('commonText.scenarios') ? (
				<>
					<RegularText
						title={generalStepTranslate('commonText.scenarios')}
						styles={{
							fontWeight: '700',
							marginBottom: '8px',
							paddingLeft: '16px',
						}}
					/>
					{scenarios.map((scenarioItem: IUserStoryScenario, index) => (
						<Box key={index} className={styles.dropdownItemContainer}>
							<RegularText title={scenarioItem.title || ''} styles={{ fontWeight: '700' }} />
							<RegularText title={scenarioItem.description || ''} />
						</Box>
					))}
				</>
			) : null}
			{!isUseCases &&
			acceptanceCriteria.length &&
			activeTab === generalStepTranslate('commonText.acceptanceCriteria') ? (
				<>
					<RegularText
						title={generalStepTranslate('commonText.acceptanceCriteria')}
						styles={{
							fontWeight: '700',
							marginBottom: '8px',
							paddingLeft: '16px',
						}}
					/>
					{acceptanceCriteria.map((scenarioItem: IUserStoryScenario, index) => (
						<Box key={index} className={styles.dropdownItemContainer}>
							<RegularText title={scenarioItem.title || ''} styles={{ fontWeight: '700' }} />
							<RegularText title={scenarioItem.description || ''} />
						</Box>
					))}
				</>
			) : null}
		</Box>
	);
};
